// Shared drawing surface for the live whiteboard.
// KEEP IN SYNC with the dashboard original.
//
// Coordinates are normalized 0..1 against the canvas box; stroke widths and
// font sizes are reference pixels at a 1280px-wide board, scaled on render,
// so every client sees the same drawing regardless of screen size.
import { useCallback, useEffect, useRef, useState } from "react";
import { WS_TYPES, newOpId } from "../wsProtocol";

const REF_WIDTH = 1280;
const FLUSH_MS = 150;

function drawOp(ctx, op, w, h, imageCache, onImageReady) {
    const scale = w / REF_WIDTH;
    ctx.save();
    if (op.tool === "image") {
        let image = imageCache && imageCache.get(op.url);
        if (!image) {
            image = new Image();
            image.crossOrigin = "anonymous";
            image.onload = () => onImageReady && onImageReady();
            image.src = op.url;
            if (imageCache) imageCache.set(op.url, image);
        }
        if (image.complete && image.naturalWidth > 0) {
            ctx.drawImage(image, op.x * w, op.y * h, op.w * w, op.h * h);
        }
        ctx.restore();
        return;
    }
    if (op.tool === "text") {
        ctx.fillStyle = op.color || "#111";
        ctx.font = `${(op.size || 24) * scale}px sans-serif`;
        ctx.textBaseline = "top";
        ctx.fillText(op.text, op.x * w, op.y * h);
        ctx.restore();
        return;
    }

    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    if (op.tool === "eraser") {
        ctx.globalCompositeOperation = "destination-out";
        ctx.lineWidth = (op.size || 30) * scale;
        ctx.strokeStyle = "rgba(0,0,0,1)";
    } else if (op.tool === "hl") {
        ctx.globalAlpha = 0.35;
        ctx.lineWidth = (op.size || 18) * scale;
        ctx.strokeStyle = op.color || "#ffeb3b";
    } else {
        ctx.lineWidth = (op.size || 4) * scale;
        ctx.strokeStyle = op.color || "#111";
    }

    ctx.beginPath();
    const points = op.points;
    ctx.moveTo(points[0][0] * w, points[0][1] * h);
    if (points.length === 1) {
        ctx.lineTo(points[0][0] * w + 0.01, points[0][1] * h);
    }
    for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i][0] * w, points[i][1] * h);
    }
    ctx.stroke();
    ctx.restore();
}

// board background presets; line colors adapt to the ground
const BG_STYLES = {
    white: { backgroundColor: "#ffffff" },
    black: { backgroundColor: "#1b2430" },
    grid: {
        backgroundColor: "#ffffff",
        backgroundImage:
            "linear-gradient(rgba(0,0,0,0.09) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.09) 1px, transparent 1px)",
        backgroundSize: "40px 40px",
    },
    lines: {
        backgroundColor: "#ffffff",
        backgroundImage: "linear-gradient(rgba(0,0,0,0.12) 1px, transparent 1px)",
        backgroundSize: "100% 52px",
        backgroundPosition: "0 51px",
    },
};

const BoardCanvas = ({
    boardId,
    boardsRef,
    subscribe,
    send,
    canDraw,
    tool,
    isTeacher,
    textPromptLabel,
    bg = "white",
    transparent = false,
}) => {
    const containerRef = useRef(null);
    const canvasRef = useRef(null);
    const imageCacheRef = useRef(new Map());
    const renderedCountRef = useRef(0);
    const strokeRef = useRef(null); // {op-in-progress points buffer, lastPoint, flushTimer}
    const pointerThrottleRef = useRef(0);
    const [remotePointer, setRemotePointer] = useState(null);
    const pointerHideRef = useRef(null);

    const toolRef = useRef(tool);
    toolRef.current = tool;
    const canDrawRef = useRef(canDraw);
    canDrawRef.current = canDraw;
    const boardIdRef = useRef(boardId);

    const replayAll = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        const ops = boardsRef.current[boardIdRef.current] || [];
        for (const op of ops) {
            drawOp(ctx, op, canvas.width, canvas.height, imageCacheRef.current, replayAllRef.current);
        }
        renderedCountRef.current = ops.length;
    }, [boardsRef]);
    const replayAllRef = useRef(replayAll);
    replayAllRef.current = replayAll;

    const drawIncremental = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        const ops = boardsRef.current[boardIdRef.current] || [];
        for (let i = renderedCountRef.current; i < ops.length; i++) {
            drawOp(ctx, ops[i], canvas.width, canvas.height, imageCacheRef.current, replayAllRef.current);
        }
        renderedCountRef.current = ops.length;
    }, [boardsRef]);

    // board switch -> full replay
    useEffect(() => {
        boardIdRef.current = boardId;
        renderedCountRef.current = 0;
        replayAll();
    }, [boardId, replayAll]);

    // resize -> resample canvas + replay
    useEffect(() => {
        const container = containerRef.current;
        const canvas = canvasRef.current;
        if (!container || !canvas) return;

        const resize = () => {
            const rect = container.getBoundingClientRect();
            canvas.width = Math.max(1, Math.round(rect.width));
            canvas.height = Math.max(1, Math.round(rect.height));
            renderedCountRef.current = 0;
            replayAll();
        };
        resize();
        const observer = new ResizeObserver(resize);
        observer.observe(container);
        return () => observer.disconnect();
    }, [replayAll]);

    // relay events
    useEffect(() => {
        return subscribe((msg) => {
            if (msg.t === WS_TYPES.SNAPSHOT) {
                renderedCountRef.current = 0;
                replayAll();
                return;
            }
            if (msg.t === WS_TYPES.POINTER) {
                setRemotePointer({ x: msg.x, y: msg.y });
                if (pointerHideRef.current) clearTimeout(pointerHideRef.current);
                pointerHideRef.current = setTimeout(() => setRemotePointer(null), 2000);
                return;
            }
            if (msg.board !== boardIdRef.current) return;
            if (msg.t === WS_TYPES.DRAW_OPS) {
                drawIncremental();
            } else if (msg.t === WS_TYPES.DRAW_UNDO || msg.t === WS_TYPES.DRAW_CLEAR) {
                renderedCountRef.current = 0;
                replayAll();
            }
        });
    }, [subscribe, replayAll, drawIncremental]);

    const toNormalized = (event) => {
        const rect = canvasRef.current.getBoundingClientRect();
        return [
            Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1),
            Math.min(Math.max((event.clientY - rect.top) / rect.height, 0), 1),
        ];
    };

    const commitOp = (op) => {
        const ops = boardsRef.current[boardIdRef.current] || (boardsRef.current[boardIdRef.current] = []);
        ops.push(op);
        send({ t: WS_TYPES.DRAW_OPS, board: boardIdRef.current, ops: [op] });
        // already painted live — keep the incremental cursor in sync
        renderedCountRef.current = ops.length;
    };

    const flushStroke = () => {
        const stroke = strokeRef.current;
        // nothing new since the last flush (only the continuity point remains)
        if (!stroke || stroke.points.length < 2) return;
        const currentTool = stroke.tool;
        const op = {
            id: newOpId(),
            tool: currentTool.name,
            color: currentTool.color,
            size: currentTool.size,
            points: stroke.points,
        };
        commitOp(op);
        stroke.flushed = true;
        // start the next segment from the last point for continuity
        stroke.points = [stroke.points[stroke.points.length - 1]];
    };

    const onPointerDown = (event) => {
        const currentTool = toolRef.current;
        if (currentTool.name === "laser" || currentTool.name === "widget") return;
        if (!canDrawRef.current) return;
        event.preventDefault();
        canvasRef.current.setPointerCapture(event.pointerId);

        const point = toNormalized(event);

        if (currentTool.name === "stamp") {
            // stamps are plain text ops carrying an emoji — they inherit
            // sync, replay, and per-author undo for free
            const stampSize = currentTool.stampSize || 64;
            commitOp({
                id: newOpId(),
                tool: "text",
                color: currentTool.color,
                size: stampSize,
                x: Math.max(0, point[0] - (stampSize / REF_WIDTH) / 2),
                y: Math.max(0, point[1] - (stampSize / REF_WIDTH) / 2),
                text: currentTool.emoji || "⭐",
            });
            replayAllSoon(replayAll);
            return;
        }

        if (currentTool.name === "text") {
            const text = window.prompt(textPromptLabel || "Text");
            if (text) {
                commitOp({
                    id: newOpId(),
                    tool: "text",
                    color: currentTool.color,
                    size: currentTool.size * 6,
                    x: point[0],
                    y: point[1],
                    text,
                });
                replayAllSoon(replayAll);
            }
            return;
        }

        strokeRef.current = {
            tool: currentTool,
            points: [point],
            lastPaintedPoint: point,
            flushTimer: setInterval(flushStroke, FLUSH_MS),
        };
    };

    const onPointerMove = (event) => {
        const currentTool = toolRef.current;

        if (currentTool.name === "laser" && isTeacher) {
            const now = Date.now();
            if (now - pointerThrottleRef.current > 30) {
                pointerThrottleRef.current = now;
                const point = toNormalized(event);
                send({ t: WS_TYPES.POINTER, x: point[0], y: point[1] });
                setRemotePointer({ x: point[0], y: point[1] });
                if (pointerHideRef.current) clearTimeout(pointerHideRef.current);
                pointerHideRef.current = setTimeout(() => setRemotePointer(null), 2000);
            }
            return;
        }

        const stroke = strokeRef.current;
        if (!stroke) return;
        const point = toNormalized(event);
        stroke.points.push(point);

        // paint the new segment immediately
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        drawOp(ctx, {
            tool: stroke.tool.name,
            color: stroke.tool.color,
            size: stroke.tool.size,
            points: [stroke.lastPaintedPoint, point],
        }, canvas.width, canvas.height);
        stroke.lastPaintedPoint = point;
    };

    const endStroke = () => {
        const stroke = strokeRef.current;
        if (!stroke) return;
        clearInterval(stroke.flushTimer);
        // send the tail segment; a plain click (single point, never flushed) is a dot
        if (stroke.points.length > 1 || !stroke.flushed) {
            const currentTool = stroke.tool;
            commitOp({
                id: newOpId(),
                tool: currentTool.name,
                color: currentTool.color,
                size: currentTool.size,
                points: stroke.points,
            });
        }
        strokeRef.current = null;
    };

    return (
        <div
            ref={containerRef}
            style={{
                position: "relative",
                width: "100%",
                height: "100%",
                ...(transparent ? { backgroundColor: "transparent" } : (BG_STYLES[bg] || BG_STYLES.white)),
                touchAction: "none",
                overflow: "hidden",
            }}
        >
            <canvas
                ref={canvasRef}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block" }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={endStroke}
                onPointerCancel={endStroke}
                onPointerLeave={endStroke}
            />
            {remotePointer && (
                <div
                    style={{
                        position: "absolute",
                        left: `${remotePointer.x * 100}%`,
                        top: `${remotePointer.y * 100}%`,
                        width: 14,
                        height: 14,
                        marginLeft: -7,
                        marginTop: -7,
                        borderRadius: "50%",
                        backgroundColor: "rgba(229, 57, 53, 0.9)",
                        boxShadow: "0 0 10px 4px rgba(229, 57, 53, 0.5)",
                        pointerEvents: "none",
                        zIndex: 5,
                    }}
                />
            )}
        </div>
    );
};

// hl-joint darkening on the seam between a text commit and replay is invisible;
// schedule a light replay after commit to normalize the surface.
function replayAllSoon(replayAll) {
    setTimeout(replayAll, 0);
}

export default BoardCanvas;
